import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import { useEffect, useMemo, useRef, useState } from 'react'
import { breakpoint } from 'ui/StyledComponents/MediaBreakpoints'
import LoanCreateForm from './LoanCreateForm'
import type { SelectOptions } from '../../types'
import type { ControlledSelectableSharegroup } from '../types'
import * as notifyActions from 'state/notifyActions'
import { FormProvider, useFieldArray, useForm, useWatch } from 'react-hook-form'
import {
	createLoanDefaultValues,
	loanFinancingTypeInput,
	STEPS_FOR_CAPITAL_LOAN,
	STEPS_FOR_NON_CAPITAL_LOAN,
} from '../constants'
import { _createLoan, _updateLoan } from 'state/finance-loan.actions'
import { Button } from 'react-md'
import { PaymentProductBillingBasis } from '@viilu-web/shared/enums/finance'
import { useCompanyApartments } from 'util/hooks/useCompanyApartments'
import Stepper from 'ui/MessageService/MessageServiceProcess/Stepper'
import LoanCreateBasicInfoForm from './LoanCreateBasicInfoForm'
import LoanCreateShareGroupsForm from './LoanCreateShareGroupsForm'
import LoanFormInput from './LoanFormInput'
import {
	calculateTotalSelectedUnits,
	convertEmptyStringsToNull,
	mapLoanToFormInputs,
	mapShareholdersToSelectableSharegroups,
} from '../utils'
import type { Action } from 'ui/components/ViiluFullPageDialog'
import ViiluFullPageDialog from 'ui/components/ViiluFullPageDialog'
import { LoanStatus } from '@viilu-web/shared/enums/loans'
import LoanCreatePaymentProduct from './LoanCreatePaymentProduct'
import { loanFormSchemaBySteps } from './validation'
import type { LoanCreateFormInputs } from './validation'
import { useZodValidationResolver } from 'util/hooks/useZodValidationResolver'
import { useRhfDependentValidation } from 'util/hooks/useRhfDependentValidation'
import { exhaustiveMatchingGuard } from '@viilu-web/shared/util/exhaustiveMatchingGuard'
import type {
	CreateLoan,
	CreateLoanShareholder,
	Loan,
} from '@viilu-web/shared/types/loans'

const ContentContainer = styled.div`
	display: flex;
	flex-flow: column nowrap;
	height: 100%;

	@media ${breakpoint.desktop} {
		flex-flow: row nowrap;
	}
`
const MainContentContainer = styled.section`
	width: 100%;
`

const StepContentContainer = styled.div`
	padding: 1rem;
`

type LoanCreateDialogProps = {
	onHide: () => void
	companyOptions: SelectOptions[]
	refreshLoans: () => void
	initialLoan?: Loan
}

function LoanCreateDialog(props: LoanCreateDialogProps) {
	const { onHide, companyOptions, initialLoan, refreshLoans } = props
	const selectedFinanceCompanyUUID = useSelector(
		({ app }: any) => app?.selectedFinanceCompanyUUID,
	)
	const { companyApartments } = useCompanyApartments(selectedFinanceCompanyUUID)
	const dispatch = useDispatch()
	const intl = useIntl()
	const [processing, setProcessing] = useState(false)
	const [paymentProductUUID, setPaymentProductUUID] = useState<
		string | undefined
	>(undefined)
	const [formStep, setFormStep] = useState(
		initialLoan?.currentFormStep ? initialLoan.currentFormStep : 0,
	)
	const [reachedStepIndex, setReachedStepIndex] = useState(
		initialLoan?.reachedFormStep ? initialLoan.reachedFormStep : 0,
	)
	const [steps, setSteps] = useState(STEPS_FOR_CAPITAL_LOAN(intl))
	const initialSelectableApartmentsComputed = useRef(false)

	const selectableSharegroupsMapped = useMemo(() => {
		const selected = mapShareholdersToSelectableSharegroups(
			companyApartments,
			initialLoan?.shareholders,
		)
		return selected
	}, [companyApartments, initialLoan?.shareholders])

	const currentStepFormSchema = loanFormSchemaBySteps(intl)[formStep]
	const resolver = useZodValidationResolver<LoanCreateFormInputs>(
		currentStepFormSchema,
	)

	const formMethods = useForm<LoanCreateFormInputs>({
		resolver,
		defaultValues: {
			...(initialLoan
				? {
						...mapLoanToFormInputs(initialLoan),
				  }
				: createLoanDefaultValues),
			selectableSharegroups: selectableSharegroupsMapped,
			companyUUID: selectedFinanceCompanyUUID,
		},
		mode: 'onBlur',
		shouldUnregister: false,
	})
	useRhfDependentValidation(formMethods.control, formMethods.trigger, [
		{
			field: 'endDateEstimated',
			dependsOn: ['startDateEstimated'],
		},
		{
			field: 'endDate',
			dependsOn: ['startDate'],
		},
	])

	const { fields: selectableSharegroups, update: updateSelectableSharegroups } =
		useFieldArray({
			control: formMethods.control,
			name: 'selectableSharegroups',
		})

	const [loanUUID, financingType] = formMethods.watch(['uuid', 'financingType'])

	const [
		billingBasis,
		originalLoanAmount,
		amountDrawn,
		watchedSelectableSharegroups,
		startDateEstimated,
		endDateEstimated,
		startDate,
		endDate,
	] = useWatch({
		control: formMethods.control,
		name: [
			'billingBasis',
			'originalLoanAmount',
			'amountDrawn',
			'selectableSharegroups',
			'startDateEstimated',
			'endDateEstimated',
			'startDate',
			'endDate',
		],
	})

	useEffect(() => {
		if (startDateEstimated.length > 0) {
			formMethods.setValue('startDate', startDateEstimated)
			formMethods.setValue('dateDrawn', startDateEstimated)
		}
		if (endDateEstimated.length > 0) {
			formMethods.setValue('endDate', endDateEstimated)
		}
	}, [startDateEstimated, endDateEstimated, formMethods])

	useEffect(() => {
		if (startDate.length > 0 && endDate.length > 0) {
			const yearsDifference =
				new Date(endDate).getFullYear() - new Date(startDate).getFullYear()
			const durationText = intl.formatMessage(
				{
					defaultMessage:
						'{years, plural, =0 {0 vuotta} one {# vuosi} other {# vuotta}}',
					description: 'Loan duration in years',
				},
				{
					years: yearsDifference,
				},
			)
			if (yearsDifference > 0) {
				formMethods.setValue('duration', durationText)
			}
		}
	}, [startDate, endDate, formMethods, intl])

	useEffect(() => {
		if (originalLoanAmount > 0) {
			formMethods.setValue('amountDrawn', originalLoanAmount)
		}
	}, [originalLoanAmount, formMethods])

	const selectedSharegroups = watchedSelectableSharegroups.filter(
		(sg) => sg.selected,
	)

	const controlledSelectedSharegroups: ControlledSelectableSharegroup[] =
		selectableSharegroups.map((field, index) => {
			return {
				...field,
				...watchedSelectableSharegroups[index],
				_trueIndex: index,
			}
		})

	useEffect(() => {
		// In case we open a draft loan that is in progress,
		// we need to reset the form to include the mapped apartments/share groups
		// that have been computed after form initialization
		if (
			!initialSelectableApartmentsComputed.current &&
			selectableSharegroupsMapped.length > 0
		) {
			formMethods.reset((prevValues) => ({
				...prevValues,
				selectableSharegroups: selectableSharegroupsMapped,
			}))
			initialSelectableApartmentsComputed.current = true
		}
	}, [selectableSharegroupsMapped, formMethods])

	useEffect(() => {
		if (financingType !== 'capital') {
			setSteps(STEPS_FOR_NON_CAPITAL_LOAN(intl))
		} else setSteps(STEPS_FOR_CAPITAL_LOAN(intl))
	}, [financingType, intl])

	const modifyLoan = async (
		data: LoanCreateFormInputs,
		turnActive: boolean,
		updateLoan: boolean,
		silentSave: boolean,
	) => {
		const companyName = companyOptions.find(
			(option) => option.value === data.companyUUID,
		)?.label
		const sanitizedData = convertEmptyStringsToNull(data)
		let totalUnits = 0

		const totalSelectedUnits = calculateTotalSelectedUnits(
			data.selectableSharegroups,
			billingBasis,
		)

		const shareholders: CreateLoanShareholder[] = data.selectableSharegroups
			.filter((sg) => sg.selected)
			.map((sg) => {
				let units = 0
				let locked = false
				switch (data.billingBasis) {
					case PaymentProductBillingBasis.AREA: {
						units = sg.billingBasisSquares.amount || 0
						locked = sg.billingBasisSquares.locked
						break
					}
					case PaymentProductBillingBasis.AREA_ADJUSTED: {
						units = sg.billingBasisWeightedSquares.amount || 0
						locked = sg.billingBasisWeightedSquares.locked
						break
					}
					case PaymentProductBillingBasis.SHARE_COUNT: {
						units = sg.billingBasisShares.amount || 0
						locked = sg.billingBasisShares.locked
						break
					}
					case PaymentProductBillingBasis.RESIDENT_COUNT: {
						units = sg.billingBasisResidents.amount || 0
						locked = sg.billingBasisResidents.locked
						break
					}
					case PaymentProductBillingBasis.PIECES: {
						units = sg.billingBasisPieces.amount || 0
						locked = sg.billingBasisPieces.locked
						break
					}
					default: {
						exhaustiveMatchingGuard(data.billingBasis)
						break
					}
				}
				const loanShareAmount =
					sg.selected && totalSelectedUnits > 0
						? (units / totalSelectedUnits) * amountDrawn
						: 0
				totalUnits += units
				return {
					loanName: sanitizedData.name,
					currency: 'EUR',
					originalUnits: units,
					locked,
					billingBasis: data.billingBasis,
					billingBasisSquares: sg.billingBasisSquares,
					billingBasisWeightedSquares: sg.billingBasisWeightedSquares,
					billingBasisShares: sg.billingBasisShares,
					billingBasisResidents: sg.billingBasisResidents,
					billingBasisPieces: sg.billingBasisPieces,
					shareGroupUUID: sg.uuid,
					originalLoanShareAmount: loanShareAmount,
					contractUUID: sg.contractUUID,
				}
			})

		// Construct the final loan object that will be sent to backend
		const createLoanData: CreateLoan = {
			name: sanitizedData.name,
			referenceNumber: sanitizedData.referenceNumber,
			status: turnActive ? LoanStatus.ACTIVE : LoanStatus.DRAFT,
			currency: 'EUR',
			companyUUID: sanitizedData.companyUUID,
			companyName: companyName || '',
			financingType: sanitizedData.financingType,
			type: sanitizedData.type,
			grantedBy: sanitizedData.grantedBy,
			duration: sanitizedData.duration,
			startDateEstimated: sanitizedData.startDateEstimated,
			endDateEstimated: sanitizedData.endDateEstimated,
			startDate: sanitizedData.startDate,
			endDate: sanitizedData.endDate,
			originalLoanAmount: Number(sanitizedData.originalLoanAmount),
			amountDrawn: Number(sanitizedData.amountDrawn),
			dateDrawn: sanitizedData.dateDrawn,
			sharesPayable: sanitizedData.sharesPayable,
			sharePaymentDates: sanitizedData.sharePaymentDates || [],
			usableOnBuildingManagerCertificate:
				sanitizedData.usableOnBuildingManagerCertificate,
			drawn: false,
			interestDaysMonth: sanitizedData.interestDaysMonth,
			interestDaysYear: sanitizedData.interestDaysYear,
			interestType: sanitizedData.interestType,
			startingInterestPercentage: Number(
				sanitizedData.startingInterestPercentage,
			),
			interestFirstDueDate: sanitizedData.interestFirstDueDate,
			interestInspectionDates: sanitizedData.interestInspectionDates,
			marginalPercentage: Number(sanitizedData.marginalPercentage),
			amortizationAmount: Number(sanitizedData.amortizationAmount),
			amortizationFirstDueDate: sanitizedData.amortizationFirstDueDate,
			amortizationDates: sanitizedData.amortizationDates,
			startingUnits: totalUnits,
			billingBasis: sanitizedData.billingBasis,
			currentFormStep: !turnActive ? formStep : undefined,
			reachedFormStep: !turnActive ? reachedStepIndex : undefined,
			shareholders: shareholders,
			startingBalance: sanitizedData.amountDrawn,
			paymentProductUUID,
		}

		try {
			let result
			if (updateLoan && data?.uuid) {
				result = await _updateLoan(
					data.uuid,
					createLoanData,
					turnActive ? paymentProductUUID : undefined,
				)
			} else {
				result = await _createLoan(
					createLoanData,
					turnActive ? paymentProductUUID : undefined,
				)
			}
			if (result?.ok) {
				if (!turnActive) {
					// If we saved draft loan, update form loan uuid if it is not yet defined
					// This is done to prevent later form stages failing since they use loanUUID
					const res = await result.json()
					if (res.loan && !loanUUID) {
						formMethods.setValue('uuid', res.loan.uuid)
					}
				}
				refreshLoans()
				if (!silentSave) {
					dispatch(
						notifyActions.success(
							updateLoan && !turnActive
								? intl.formatMessage({
										defaultMessage:
											'Lainan luonnos tallennettiin onnistuneesti.',
										description:
											'Notification message when draft loan has been succesfully saved',
								  })
								: intl.formatMessage({
										defaultMessage: 'Laina luotiin onnistuneesti.',
										description:
											'Notification message when loan has been succesfully created',
								  }),
						),
					)
					onHide()
				}
			} else {
				dispatch(
					notifyActions.error(
						updateLoan && !turnActive
							? intl.formatMessage({
									defaultMessage: 'Lainan luonnoksen tallennus ei onnistunut.',
									description:
										'Notification message when there was error during draft loan saving',
							  })
							: intl.formatMessage({
									defaultMessage: 'Lainan luonti ei onnistunut.',
									description:
										'Notification message when there was error during loan creation',
							  }),
					),
				)
				throw new Error('Response is not ok.')
			}
		} catch (error) {
			console.error(error)
			setProcessing(false)
		}
	}

	const onDraftSave = async (silentSave: boolean) => {
		const currentData = formMethods.getValues()
		if (loanUUID) {
			// Update loan
			modifyLoan(currentData, false, true, silentSave)
		} else {
			// Create loan as draft
			modifyLoan(currentData, false, false, silentSave)
		}
	}

	const onSubmit = formMethods.handleSubmit(async (data) => {
		setProcessing(true)
		if (loanUUID) modifyLoan(data, true, true, false)
		else modifyLoan(data, true, false, false)
	})

	const moveToStep = async (stepIndex: number) => {
		// If we are moving to a higher step, validate the form to prevent
		// user from moving on with invalid fields
		if (stepIndex > formStep) {
			// Validate all steps from current up to the one we're trying to move to
			const schemas = loanFormSchemaBySteps(intl)
			const values = formMethods.getValues()
			for (let i = formStep; i < stepIndex; i++) {
				const schema = schemas[i]
				if (!schema) break
				const result = await schema.safeParseAsync(values)

				if (!result.success) {
					// Show error, set current form step to the failing step
					dispatch(
						notifyActions.error(
							intl.formatMessage({
								defaultMessage: 'Täytä vaaditut kentät ennen etenemistä.',
								description:
									'Validation error when there are invalid fields in form',
							}),
						),
					)
					setFormStep(i)
					formMethods.trigger()
					return
				}
			}
			setFormStep(stepIndex)
			if (reachedStepIndex < stepIndex) {
				setReachedStepIndex(stepIndex)
			}
			if (stepIndex > 1) {
				onDraftSave(true)
			}
		} else {
			// We are moving to a lower step, dont need to validate
			setFormStep(stepIndex)
		}
	}

	const renderLoanFinancialTypeStep = () => {
		return (
			<StepContentContainer>
				<LoanFormInput input={loanFinancingTypeInput} />
			</StepContentContainer>
		)
	}

	const renderMainElement = () => {
		return (
			<MainContentContainer>
				<h2
					className="md-title"
					style={{ marginBottom: '0', padding: '1rem 1.5rem' }}
				>
					{steps[formStep].label}

					<hr style={{ borderColor: 'rgba(0, 0, 0, 0.2)' }} />
				</h2>
				<FormProvider {...formMethods}>
					{formStep === 0 && renderLoanFinancialTypeStep()}
					{formStep === 1 && (
						<LoanCreateBasicInfoForm companyOptions={companyOptions} />
					)}
					{formStep === 2 && <LoanCreateForm />}
					{formStep === 3 && (
						<LoanCreateShareGroupsForm
							companyOptions={companyOptions}
							sharegroups={controlledSelectedSharegroups}
							onUpdateSelectableSharegroups={updateSelectableSharegroups}
						/>
					)}
					{formStep === 4 && (
						<LoanCreatePaymentProduct
							onPaymentProductDefined={(uuid) => setPaymentProductUUID(uuid)}
							sharegroups={controlledSelectedSharegroups}
							companyOptions={companyOptions}
							selectedFinanceCompanyUUID={selectedFinanceCompanyUUID}
						/>
					)}
				</FormProvider>
			</MainContentContainer>
		)
	}

	const allContractsDefined =
		selectedSharegroups.filter(
			(sg) => sg.contractUUID !== null && sg.contractUUID !== undefined,
		).length === selectedSharegroups.length

	const renderActions = () => {
		const noSharegroupSelection =
			formStep === 3 && !controlledSelectedSharegroups.some((sg) => sg.selected)
		// Restrict moving on from sharegroup selection if there are 0 units anywhere in selection
		const invalidUnitAmounts =
			formStep === 3 &&
			controlledSelectedSharegroups
				.filter((sg) => sg.selected)
				.some((sg) => {
					switch (billingBasis) {
						case PaymentProductBillingBasis.AREA: {
							return sg.billingBasisSquares.amount === 0
						}
						case PaymentProductBillingBasis.AREA_ADJUSTED: {
							return sg.billingBasisWeightedSquares.amount === 0
						}
						case PaymentProductBillingBasis.SHARE_COUNT: {
							return sg.billingBasisShares.amount === 0
						}
						case PaymentProductBillingBasis.RESIDENT_COUNT: {
							return sg.billingBasisResidents.amount === 0
						}
						case PaymentProductBillingBasis.PIECES: {
							return sg.billingBasisPieces.amount === 0
						}
						default: {
							return exhaustiveMatchingGuard(billingBasis, false)
						}
					}
				})

		const actions: Array<Action | undefined> = [
			formStep > 0
				? {
						id: 'create-loan-form-back',
						onClick: () => setFormStep(formStep > 0 ? formStep - 1 : 0),
						align: 'right',
						children: (
							<FormattedMessage
								defaultMessage="Takaisin"
								description="Back button"
							/>
						),
				  }
				: undefined,
			formStep > 0
				? {
						id: 'create-loan-form-save-draft',
						onClick: () => onDraftSave(false),
						secondary: true,
						align: 'left',
						children: (
							<FormattedMessage
								defaultMessage="Tallenna luonnos"
								description="Save as draft button"
							/>
						),
				  }
				: undefined,
			formStep !== steps.length - 1
				? {
						id: 'create-loan-form-next',
						raised: true,
						secondary: true,
						align: 'right',
						disabled: noSharegroupSelection || invalidUnitAmounts,
						onClick: () =>
							moveToStep(formStep < steps.length - 1 ? formStep + 1 : formStep),
						children: (
							<FormattedMessage
								defaultMessage="Seuraava"
								description="Next step button"
							/>
						),
				  }
				: {
						id: 'create-loan-form-save',
						raised: true,
						secondary: true,
						align: 'right',
						onClick: onSubmit,
						disabled:
							processing ||
							(financingType === 'capital' &&
								(!paymentProductUUID || !allContractsDefined)),
						children: (
							<FormattedMessage
								defaultMessage="Luo laina"
								description="Create loan button"
							/>
						),
				  },
		]
		const filteredActions = actions.filter(
			(action): action is Action => action !== undefined,
		)
		return filteredActions
	}

	return (
		<ViiluFullPageDialog
			id="loan-view-dialog"
			title={intl.formatMessage({
				defaultMessage: 'Luo uusi laina',
				description: 'Title for loan create dialog',
			})}
			actions={renderActions()}
			visible={true}
			onHide={onHide}
			focusOnMount={false}
			containFocus={true}
			nav={
				<Button
					icon
					onClick={() => {
						onHide()
					}}
				>
					close
				</Button>
			}
			toolbarStyle={{ background: 'var(--color-secondary-dark)' }}
			navActions={
				<Stepper
					steps={steps}
					goToStep={({ id }) =>
						moveToStep(steps.findIndex((step) => step.id === id) || 0)
					}
					activeStepIndex={formStep}
					reachedStepIndex={reachedStepIndex}
				/>
			}
		>
			<ContentContainer>{renderMainElement()}</ContentContainer>
		</ViiluFullPageDialog>
	)
}

export default LoanCreateDialog
